import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  Pressable,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { Flag } from './Flag';
import CloseIcon from '../../../../assets/images/CloseIcon.svg';
import DownArrowIcon from '../../../../assets/images/DownArrowIcon.svg';
import RadioSelectedIcon from '../../../../assets/images/RadioSelectButton.svg';
import Colors from '../constants/colors';

export type DashboardLanguage = 'english' | 'spanish';

type Props = {
  selectedLanguage: DashboardLanguage;
  onSelectLanguage: (language: DashboardLanguage) => void;
  disabled?: boolean;
};

const LANGUAGE_OPTIONS: DashboardLanguage[] = ['english', 'spanish'];

const DashboardLocationLanguageSelector: React.FC<Props> = ({
  selectedLanguage,
  onSelectLanguage,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);

  const closeModal = () => setModalVisible(false);

  const handleSelect = (language: DashboardLanguage) => {
    onSelectLanguage(language);
    closeModal();
  };

  const labelFor = (language: DashboardLanguage) =>
    language === 'english' ? t('ENGLISH') : t('SPANISH');

  const flagIso3For = (language: DashboardLanguage) =>
    language === 'english' ? 'USA' : 'MEX';

  return (
    <>
      <TouchableOpacity
        testID="locationLanguageSelectorButton"
        style={[styles.trigger, disabled && styles.triggerDisabled]}
        onPress={() => setModalVisible(true)}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={labelFor(selectedLanguage)}
      >
        <Flag iso3={flagIso3For(selectedLanguage)} width={16} height={16} />
        <Text style={styles.triggerText} numberOfLines={1}>
          {labelFor(selectedLanguage)}
        </Text>
        <DownArrowIcon width={16} height={16} />
      </TouchableOpacity>

      <Modal
        visible={modalVisible}
        transparent
        animationType="none"
        onRequestClose={closeModal}
      >
        <View style={styles.modalOverlay}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={closeModal}
            accessibilityRole="button"
            accessibilityLabel={t('CLOSE_NEED_HELP_MODAL_ACCESSIBILITY')}
          />
          <View style={styles.modalContent} testID="locationLanguageModal">
            <TouchableOpacity
              onPress={closeModal}
              testID="locationLanguageModalClose"
              style={styles.closeButtonContainer}
              accessibilityRole="button"
              accessibilityLabel={t('CLOSE_NEED_HELP_MODAL_ACCESSIBILITY')}
            >
              <CloseIcon width={24} height={24} />
            </TouchableOpacity>

            <Text style={styles.modalTitle}>{t('CHOOSE_A_LANGUAGE')}</Text>

            <View style={styles.optionsList}>
              {LANGUAGE_OPTIONS.map((language) => {
                const isSelected = selectedLanguage === language;
                return (
                  <TouchableOpacity
                    key={language}
                    testID={
                      language === 'english'
                        ? 'locationLanguageModalEnglish'
                        : 'locationLanguageModalSpanish'
                    }
                    style={[
                      styles.optionRow,
                      isSelected && styles.optionRowSelected,
                    ]}
                    onPress={() => handleSelect(language)}
                    disabled={disabled}
                  >
                    <View style={styles.optionLabelRow}>
                      {/* <Flag iso3={flagIso3For(language)} width={24} height={24} /> */}
                      <Text style={[styles.optionLabel, isSelected && { fontWeight: 500 }]}>{labelFor(language)}</Text>
                    </View>
                    {isSelected ? (
                      <RadioSelectedIcon width={24} height={24} />
                    ) : (
                      <View style={styles.radioUnselected} />
                    )}
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

export default DashboardLocationLanguageSelector;

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#E8E8E8',
    borderRadius: 8,
    padding: 8,
    maxWidth: 130,
  },
  triggerDisabled: {
    opacity: 0.5,
  },
  triggerText: {
    flexShrink: 1,
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 22,
    color: Colors.secondaryText,
  },
  modalOverlay: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  modalContent: {
    width: '100%',
    flexShrink: 1,
    backgroundColor: Colors.white,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  closeButtonContainer: {
    alignItems: 'flex-end',
    marginVertical: 16,
  },
  modalTitle: {
    fontFamily: 'Rubik',
    fontSize: 24,
    fontWeight: '500',
    lineHeight: 30,
    color: Colors.titleTextColor,
    marginBottom: 24,
  },
  optionsList: {
    marginHorizontal: -24,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 56,
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  optionRowSelected: {
    backgroundColor: '#FAFAFA',
  },
  optionLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  optionLabel: {
    fontFamily: 'Rubik',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.titleTextColor,
  },
  radioUnselected: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: Colors.borderGrey,
    backgroundColor: Colors.white,
  },
});
